import { UNASSIGNED_PRACTITIONER_ID, UNASSIGNED_SERVICE_ID } from './engine';
import { METRIC_BY_ID } from './metrics';
import type { ReportResult, ReportRow, ReportValue } from './types';

export const KEY_FIGURE_ORDER = [
  'revenue',
  'commission_amount',
  'appointment_count',
  'unique_patients',
  'new_patients',
  'returning_patients',
  'retention_rate',
  'completed_appointments',
  'paid_amount',
] as const;

export const FINANCIAL_METRICS = [
  'revenue',
  'commission_amount',
  'commission_percentage',
  'average_revenue_per_appointment',
  'revenue_per_appointment',
  'billable_appointments',
  'discounts',
  'refunds',
  'product_sales',
  'outstanding_amount',
  'paid_amount',
  'payment_method_split',
  'revenue_per_working_day',
  'revenue_per_practitioner',
] as const;

export const APPOINTMENT_METRICS = [
  'appointment_count',
  'completed_appointments',
  'cancelled_appointments',
  'no_shows',
  'average_appointment_length',
  'utilisation_rate',
  'average_appointments_per_working_day',
  'appointment_completion_rate',
  'cancellation_rate',
  'no_show_rate',
] as const;

export const PATIENT_METRICS = [
  'unique_patients',
  'new_patients',
  'returning_patients',
  'average_visits_per_patient',
  'patients_seen_per_practitioner',
  'new_patient_percentage',
  'new_patient_acquisition',
  'returning_patient_percentage',
] as const;

export const COMMISSION_TABLE_METRICS = [
  'revenue',
  'commission_percentage',
  'commission_amount',
] as const;

export const SERVICE_TABLE_METRICS = [
  'appointment_count',
  'treatments_per_service',
  'unique_patients',
  'revenue',
  'revenue_per_service',
  'average_revenue_per_appointment',
  'average_revenue_per_service',
  'commission_percentage',
  'commission_amount',
] as const;

export const COMPARISON_METRICS = [
  'appointment_count',
  'unique_patients',
  'revenue',
  'commission_amount',
  'retention_rate',
  'new_patients',
  'completed_appointments',
] as const;

const MAX_TABLE_COLUMNS = 5;
const MAX_KEY_FIGURES = 5;

export interface PdfFigure {
  metricId: string;
  value: ReportValue;
}

export interface PdfTableColumn {
  metricId: string;
}

export interface PdfTable {
  title: string | null;
  identityKind: 'practitioner' | 'service';
  columns: PdfTableColumn[];
  rows: ReportRow[];
  totals: ReportRow | null;
}

export interface PdfMixItem {
  label: string;
  share: number;
}

export interface PdfSection {
  kind:
    | 'financial'
    | 'commission'
    | 'appointments'
    | 'patients'
    | 'retention'
    | 'services'
    | 'comparison'
    | 'mix'
    | 'other';
  figures: PdfFigure[];
  tables: PdfTable[];
  mix?: PdfMixItem[];
  note?: 'retention' | 'commission_unconfigured';
}

export interface PractitionerPdfPlan {
  keyFigures: PdfFigure[];
  sections: PdfSection[];
  practitionerBlocks: {
    row: ReportRow;
    tables: PdfTable[];
  }[];
  includeDetails: boolean;
  commissionConfigured: boolean;
}

function selectedOf(ids: readonly string[], selected: Set<string>): string[] {
  return ids.filter((id) => selected.has(id));
}

function pickColumns(candidates: readonly string[], selected: Set<string>, max = MAX_TABLE_COLUMNS): string[] {
  return selectedOf(candidates, selected).slice(0, max);
}

function figuresFrom(ids: string[], row: ReportRow): PdfFigure[] {
  return ids.map((metricId) => ({ metricId, value: row.values[metricId] ?? null }));
}

function serviceRows(row: ReportRow): ReportRow[] {
  return row.children.filter((child) => child.grouping === 'service');
}

function practitionerParents(result: ReportResult): ReportRow[] {
  return result.rows.filter((row) => row.grouping === 'practitioner');
}

function serviceParents(result: ReportResult): ReportRow[] {
  return result.rows.filter((row) => row.grouping === 'service');
}

function leftoverMetrics(selected: Set<string>, used: Set<string>): string[] {
  return [...selected].filter((id) => !used.has(id) && METRIC_BY_ID[id]);
}

function isUnassignedRow(row: ReportRow): boolean {
  return (
    row.practitionerId === UNASSIGNED_PRACTITIONER_ID ||
    row.serviceId === UNASSIGNED_SERVICE_ID ||
    row.id.includes('unassigned')
  );
}

export function isMeaningfulRow(row: ReportRow): boolean {
  if (row.factCount <= 0) return false;
  if (!isUnassignedRow(row)) return true;
  return Object.values(row.values).some((value) => {
    if (value == null || value === '') return false;
    if (typeof value === 'number') return value !== 0;
    return String(value).trim().length > 0;
  });
}

function meaningful(rows: ReportRow[]): ReportRow[] {
  return rows.filter(isMeaningfulRow);
}

function mergeMetric(metricId: string, rows: ReportRow[]): ReportValue {
  const format = METRIC_BY_ID[metricId]?.format;
  if (format === 'percent') {
    let weighted = 0;
    let total = 0;
    for (const row of rows) {
      const value = row.values[metricId];
      const weight =
        typeof row.values.revenue === 'number' && row.values.revenue > 0
          ? row.values.revenue
          : typeof row.values.appointment_count === 'number'
            ? row.values.appointment_count
            : row.factCount;
      if (typeof value === 'number' && weight > 0) {
        weighted += value * weight;
        total += weight;
      }
    }
    return total > 0 ? weighted / total : null;
  }
  if (format === 'text') return rows.find((row) => row.values[metricId])?.values[metricId] ?? null;
  let sum = 0;
  let any = false;
  for (const row of rows) {
    const value = row.values[metricId];
    if (typeof value === 'number') {
      sum += value;
      any = true;
    }
  }
  return any ? sum : null;
}

function mergeServiceRows(practitioners: ReportRow[], metricIds: string[]): ReportRow[] {
  const groups = new Map<string, ReportRow[]>();
  for (const practitioner of practitioners) {
    for (const child of meaningful(serviceRows(practitioner))) {
      const key = child.serviceId || child.label;
      const list = groups.get(key) ?? [];
      list.push(child);
      groups.set(key, list);
    }
  }
  return [...groups.values()].map((rows) => {
    const first = rows[0];
    const values: Record<string, ReportValue> = {};
    for (const metricId of metricIds) values[metricId] = mergeMetric(metricId, rows);
    return {
      ...first,
      id: `merged-service-${first.serviceId ?? first.label}`,
      children: [],
      values,
      factCount: rows.reduce((sum, row) => sum + row.factCount, 0),
    };
  });
}

function mixFromRows(rows: ReportRow[]): PdfMixItem[] {
  const items = meaningful(rows)
    .map((row) => {
      const mix = row.values.service_mix;
      const appointments = row.values.appointment_count;
      const share = typeof mix === 'number' ? mix : typeof appointments === 'number' ? appointments : 0;
      return { label: row.label, share };
    })
    .filter((item) => item.share > 0);
  const total = items.reduce((sum, item) => sum + item.share, 0);
  if (total <= 0) return [];
  return items
    .map((item) => ({ label: item.label, share: item.share / total }))
    .sort((a, b) => b.share - a.share);
}

export function planPractitionerPdf(
  result: ReportResult,
  options: { includeDetailedRecords?: boolean } = {},
): PractitionerPdfPlan {
  const selected = new Set(result.metricIds);
  const used = new Set<string>();
  const totals = result.totals;
  const commissionConfigured = result.facts.some(
    (fact) => fact.commission_percent != null && Number(fact.commission_percent) > 0,
  );

  let keyIds = selectedOf(KEY_FIGURE_ORDER, selected);
  if (!commissionConfigured) {
    keyIds = keyIds.filter((id) => id !== 'commission_amount' && id !== 'commission_percentage');
  }
  if (keyIds.length === 0) {
    keyIds = result.metricIds.filter((id) => METRIC_BY_ID[id]?.format !== 'text').slice(0, MAX_KEY_FIGURES);
  }
  keyIds = keyIds.slice(0, MAX_KEY_FIGURES);
  keyIds.forEach((id) => used.add(id));

  const sections: PdfSection[] = [];
  const grouping = result.grouping;
  const hasServiceGrouping =
    grouping === 'practitioner_service' || grouping === 'service' || grouping === 'service_practitioner';
  const practitioners = meaningful(practitionerParents(result));
  const expandPractitionerBlocks =
    grouping === 'practitioner_service' && (practitioners.length <= 1 || Boolean(options.includeDetailedRecords));

  const financialIds = selectedOf(FINANCIAL_METRICS, selected).filter((id) => {
    if (!commissionConfigured && (id === 'commission_amount' || id === 'commission_percentage')) return false;
    return true;
  });
  const appointmentIds = selectedOf(APPOINTMENT_METRICS, selected);
  const patientIds = selectedOf(PATIENT_METRICS, selected);
  const hasCommission = selected.has('commission_amount') || selected.has('commission_percentage');
  const hasRetention = selected.has('retention_rate');
  const serviceColumnIds = pickColumns(
    SERVICE_TABLE_METRICS.filter((id) => commissionConfigured || (id !== 'commission_amount' && id !== 'commission_percentage')),
    selected,
  );
  const commissionColumnIds = pickColumns(COMMISSION_TABLE_METRICS, selected);
  const extraFinancial = financialIds.filter((id) => {
    if (keyIds.includes(id)) return false;
    if (hasCommission && (id === 'commission_amount' || id === 'commission_percentage')) return false;
    return true;
  });

  if (practitioners.length > 1) {
    const comparisonCols = pickColumns(
      COMPARISON_METRICS.filter((id) => commissionConfigured || id !== 'commission_amount'),
      selected,
    );
    if (comparisonCols.length > 0) {
      comparisonCols.forEach((id) => used.add(id));
      sections.push({
        kind: 'comparison',
        figures: [],
        tables: [
          {
            title: null,
            identityKind: 'practitioner',
            columns: comparisonCols.map((metricId) => ({ metricId })),
            rows: practitioners,
            totals,
          },
        ],
      });
    }
  }

  if (extraFinancial.length > 0) {
    extraFinancial.forEach((id) => used.add(id));
    sections.push({
      kind: 'financial',
      figures: figuresFrom(extraFinancial, totals),
      tables: [],
    });
  }

  const commissionTable =
    hasCommission && commissionConfigured && hasServiceGrouping && commissionColumnIds.length > 0
      ? ({
          title: null,
          identityKind: 'service' as const,
          columns: commissionColumnIds.map((metricId) => ({ metricId })),
          rows: [],
          totals,
        } satisfies PdfTable)
      : null;
  if (hasCommission && !commissionConfigured) {
    used.add('commission_amount');
    used.add('commission_percentage');
    sections.push({
      kind: 'commission',
      figures: [],
      tables: [],
      note: 'commission_unconfigured',
    });
  } else if (commissionTable) {
    commissionColumnIds.forEach((id) => used.add(id));
    const commissionFigures = selectedOf(['revenue', 'commission_percentage', 'commission_amount'], selected);
    sections.push({
      kind: 'commission',
      figures: figuresFrom(commissionFigures, totals),
      tables: [commissionTable],
    });
  }

  const extraAppointments = appointmentIds.filter((id) => !keyIds.includes(id));
  extraAppointments.forEach((id) => used.add(id));
  appointmentIds.forEach((id) => used.add(id));
  if (extraAppointments.length > 0) {
    sections.push({
      kind: 'appointments',
      figures: figuresFrom(extraAppointments, totals),
      tables: [],
    });
  }

  const extraPatients = patientIds.filter((id) => !keyIds.includes(id));
  extraPatients.forEach((id) => used.add(id));
  patientIds.forEach((id) => used.add(id));
  if (extraPatients.length > 0) {
    sections.push({
      kind: 'patients',
      figures: figuresFrom(extraPatients, totals),
      tables: [],
    });
  }

  if (hasRetention) {
    used.add('retention_rate');
    const patientSection = sections.find((section) => section.kind === 'patients');
    if (patientSection) patientSection.note = 'retention';
    else if (!keyIds.includes('retention_rate')) {
      sections.push({
        kind: 'retention',
        figures: figuresFrom(['retention_rate'], totals),
        tables: [],
        note: 'retention',
      });
    }
  }

  const remainingServiceCols = serviceColumnIds.filter((id) => {
    if (commissionTable && commissionColumnIds.includes(id)) return false;
    return true;
  });
  const wantServiceTable =
    hasServiceGrouping &&
    remainingServiceCols.length > 0 &&
    (remainingServiceCols.some((id) => !commissionColumnIds.includes(id)) || !commissionTable);

  if (wantServiceTable) {
    remainingServiceCols.forEach((id) => used.add(id));
    sections.push({
      kind: 'services',
      figures: [],
      tables: [
        {
          title: null,
          identityKind: grouping === 'service_practitioner' ? 'practitioner' : 'service',
          columns: remainingServiceCols.map((metricId) => ({ metricId })),
          rows: [],
          totals,
        },
      ],
    });
  }

  if (selected.has('service_mix') || selected.has('most_common_services')) {
    used.add('service_mix');
    used.add('most_common_services');
  }

  const leftover = leftoverMetrics(selected, used);
  const leftoverService = leftover.filter((id) => (SERVICE_TABLE_METRICS as readonly string[]).includes(id));
  const leftoverOther = leftover.filter((id) => !(SERVICE_TABLE_METRICS as readonly string[]).includes(id));
  if (leftoverService.length > 0 && hasServiceGrouping) {
    leftoverService.forEach((id) => used.add(id));
    const overflowTable: PdfTable = {
      title: null,
      identityKind: grouping === 'service_practitioner' ? 'practitioner' : 'service',
      columns: leftoverService.slice(0, MAX_TABLE_COLUMNS).map((metricId) => ({ metricId })),
      rows: [],
      totals,
    };
    const existing = sections.find((section) => section.kind === 'services');
    if (existing) existing.tables.push(overflowTable);
    else sections.push({ kind: 'services', figures: [], tables: [overflowTable] });
  }
  if (leftoverOther.length > 0) {
    leftoverOther.forEach((id) => used.add(id));
    sections.push({
      kind: 'other',
      figures: figuresFrom(leftoverOther, totals),
      tables: [],
    });
  }

  const allMetricIds = result.metricIds;
  const practitionerBlocks =
    expandPractitionerBlocks
      ? practitioners.map((row) => {
          const tables: PdfTable[] = [];
          const children = meaningful(serviceRows(row));
          if (commissionTable) {
            tables.push({
              ...commissionTable,
              rows: children,
              totals: row,
            });
          }
          const serviceSection = sections.find((section) => section.kind === 'services');
          if (serviceSection) {
            for (const table of serviceSection.tables) {
              if (table.columns.length === 0) continue;
              tables.push({
                ...table,
                rows: children,
                totals: row,
              });
            }
          }
          return { row, tables };
        })
      : [];

  if (grouping === 'service' || grouping === 'service_practitioner') {
    const rows =
      grouping === 'service'
        ? meaningful(serviceParents(result))
        : meaningful(
            result.rows.flatMap((row) =>
              row.grouping === 'service' ? [row] : row.children.filter((child) => child.grouping === 'practitioner'),
            ),
          );
    for (const section of sections) {
      if (section.kind === 'commission' && section.tables[0]) {
        section.tables[0] = {
          ...section.tables[0],
          identityKind: grouping === 'service' ? 'service' : 'practitioner',
          rows,
        };
      }
      if (section.kind === 'services') {
        section.tables = section.tables.map((table) => ({
          ...table,
          rows,
        }));
      }
    }
    if (selected.has('service_mix')) {
      sections.push({ kind: 'mix', figures: [], tables: [], mix: mixFromRows(rows) });
    }
  }

  if (grouping === 'practitioner_service') {
    if (expandPractitionerBlocks) {
      for (const section of sections) {
        if (section.kind === 'commission' || section.kind === 'services') {
          section.tables = [];
        }
      }
      if (selected.has('service_mix') && practitioners.length === 1) {
        sections.push({
          kind: 'mix',
          figures: [],
          tables: [],
          mix: mixFromRows(serviceRows(practitioners[0])),
        });
      }
    } else {
      const merged = mergeServiceRows(practitioners, allMetricIds);
      for (const section of sections) {
        if (section.kind === 'commission' && section.tables[0]) {
          section.tables[0] = { ...section.tables[0], rows: merged };
        }
        if (section.kind === 'services') {
          section.tables = section.tables.map((table) => ({ ...table, rows: merged }));
        }
      }
      if (selected.has('service_mix')) {
        sections.push({ kind: 'mix', figures: [], tables: [], mix: mixFromRows(merged) });
      }
    }
  }

  return {
    keyFigures: figuresFrom(keyIds, totals),
    sections: sections.filter(
      (section) =>
        section.figures.length > 0 ||
        section.tables.some((table) => table.rows.length > 0) ||
        (section.mix && section.mix.length > 0) ||
        Boolean(section.note),
    ),
    practitionerBlocks: practitionerBlocks.filter((block) => block.tables.length > 0),
    includeDetails: Boolean(options.includeDetailedRecords),
    commissionConfigured,
  };
}

export function countPlannedTables(plan: PractitionerPdfPlan): number {
  return (
    plan.sections.reduce((sum, section) => sum + section.tables.length, 0) +
    plan.practitionerBlocks.reduce((sum, block) => sum + block.tables.length, 0)
  );
}
